import { useMutation } from "@apollo/client";
import { AddCircleOutline, Cancel } from "@mui/icons-material";
import {
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useNotify, useTranslate } from "react-admin";

import { refreshPortalAccess } from "../../auth/refreshPortalAccess";
import {
  AddStaffRoleDocument,
  type AddStaffRoleMutation,
  type AddStaffRoleMutationVariables,
  RemoveStaffRoleDocument,
  type RemoveStaffRoleMutation,
  type RemoveStaffRoleMutationVariables,
  StaffMembersDocument,
  type StaffMembersQuery,
  StaffRole,
} from "../../generated/graphql";
import { captureException } from "../../lib/errorHandling";
import { STAFF_ROLES, staffManagementErrorKey } from "../../lib/staffManagement";
import { color, shadows } from "../../theme/designTokens";
import { CustomConfirm } from "../CustomConfirm";

type StaffMember = StaffMembersQuery["staffMembers"][number];

type StaffMembersTableProps = {
  members: StaffMembersQuery["staffMembers"];
};

type PendingRemoval = { member: StaffMember; role: StaffRole };

type AddMenu = { anchor: HTMLElement; member: StaffMember };

export const StaffMembersTable = ({ members }: StaffMembersTableProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [addMenu, setAddMenu] = useState<AddMenu | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<PendingRemoval | null>(null);
  const [addStaffRole, { loading: adding }] = useMutation<AddStaffRoleMutation, AddStaffRoleMutationVariables>(
    AddStaffRoleDocument
  );
  const [removeStaffRole, { loading: removing }] = useMutation<
    RemoveStaffRoleMutation,
    RemoveStaffRoleMutationVariables
  >(RemoveStaffRoleDocument);
  const busy = adding || removing;

  const roleLabel = (role: StaffRole) => translate(`staffRoles.${role}`);

  const reportFailure = (error: unknown, operation: string) => {
    const key = staffManagementErrorKey(error);
    if (key === null) captureException(error, operation);
    notify(translate(key ?? "team.roleChangeError"), { type: "error" });
  };

  const handleAdd = async (member: StaffMember, role: StaffRole) => {
    setAddMenu(null);
    try {
      await addStaffRole({
        variables: { userId: member.id, role },
        refetchQueries: [{ query: StaffMembersDocument }],
      });
      refreshPortalAccess();
      notify(translate("team.roleAdded", { name: member.name, role: roleLabel(role) }), { type: "success" });
    } catch (error) {
      reportFailure(error, "staff/team: add role");
    }
  };

  const handleRemove = async ({ member, role }: PendingRemoval) => {
    setPendingRemoval(null);
    try {
      await removeStaffRole({
        variables: { userId: member.id, role },
        refetchQueries: [{ query: StaffMembersDocument }],
      });
      refreshPortalAccess();
      notify(translate("team.roleRemoved", { name: member.name, role: roleLabel(role) }), { type: "success" });
    } catch (error) {
      reportFailure(error, "staff/team: remove role");
    }
  };

  const missingRoles = addMenu ? STAFF_ROLES.filter(role => !addMenu.member.roles.includes(role)) : [];

  return (
    <>
      <Paper variant="outlined" sx={{ borderColor: color.panelBorder, boxShadow: shadows.card }}>
        <TableContainer>
          <Table size="small" data-testid="staff-members">
            <TableHead>
              <TableRow>
                <TableCell>{translate("team.columns.name")}</TableCell>
                <TableCell>{translate("team.columns.email")}</TableCell>
                <TableCell>{translate("team.columns.roles")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map(member => {
                const canAdd = STAFF_ROLES.some(role => !member.roles.includes(role));
                return (
                  <TableRow key={member.id} data-testid="staff-member-row">
                    <TableCell>{member.name}</TableCell>
                    <TableCell>{member.email ?? "–"}</TableCell>
                    <TableCell>
                      <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
                        {member.roles.length === 0 && (
                          <Typography variant="body2" color="text.secondary">
                            {translate("team.noRoles")}
                          </Typography>
                        )}
                        {member.roles.map(role => {
                          const label = roleLabel(role);
                          const removeLabel = translate("team.removeRole", { role: label, name: member.name });
                          return (
                            <Chip
                              key={role}
                              size="small"
                              label={label}
                              disabled={busy}
                              onDelete={() => setPendingRemoval({ member, role })}
                              deleteIcon={<Cancel aria-label={removeLabel} aria-hidden={false} />}
                            />
                          );
                        })}
                        {canAdd && (
                          <IconButton
                            size="small"
                            disabled={busy}
                            aria-label={translate("team.addRole", { name: member.name })}
                            onClick={event => setAddMenu({ anchor: event.currentTarget, member })}
                          >
                            <AddCircleOutline fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Menu anchorEl={addMenu?.anchor} open={addMenu !== null} onClose={() => setAddMenu(null)}>
        {missingRoles.map(role => (
          <MenuItem key={role} onClick={() => addMenu && handleAdd(addMenu.member, role)}>
            {roleLabel(role)}
          </MenuItem>
        ))}
      </Menu>

      <CustomConfirm
        isOpen={pendingRemoval !== null}
        title="team.removeRoleTitle"
        content={
          pendingRemoval
            ? translate("team.removeRoleConfirm", {
                name: pendingRemoval.member.name,
                role: roleLabel(pendingRemoval.role),
              })
            : ""
        }
        onConfirm={() => pendingRemoval && handleRemove(pendingRemoval)}
        onClose={() => setPendingRemoval(null)}
      />
    </>
  );
};
